import React from 'react';
import { useQuery } from 'react-query';
import api from '../../config/api';
import { queryKeys, staleTimes } from '../../config/queryKeys';
import formatMoney from '../../utils/formatMoney';
import { formatDateOnly } from '../../utils/formatTimestamp';
import { formatCents, StatusBadge } from '../shared/Ui';
import '../erp/Erp.css';
import './Commercial.css';

export function MoneyText({ amount, currency = 'USD', unit = 'cents', className = '' }) {
  const rendered =
    unit === 'dollars'
      ? formatMoney(Number(amount) || 0, currency)
      : formatCents(Number(amount) || 0, currency);
  return <span className={`money-text${className ? ` ${className}` : ''}`}>{rendered}</span>;
}

export function DateText({ value, fallback = '—' }) {
  return <time className="date-text" dateTime={value || undefined}>{value ? formatDateOnly(value) : fallback}</time>;
}

export function PartyPicker({
  mode = 'customer',
  value,
  onChange,
  disabled = false,
  id,
  required = false,
  allowEmpty = true,
  label,
}) {
  const isVendor = mode === 'vendor';
  const key = isVendor ? queryKeys.vendors : queryKeys.serviceCustomers;
  const parties = useQuery(
    key,
    () => api.get(isVendor ? '/vendors' : '/services/customers').then((r) => r.data),
    { staleTime: staleTimes.reference }
  );
  const rows = parties.data || [];
  return (
    <label className="commercial-picker" htmlFor={id}>
      {label && <span>{label}</span>}
      <select
        id={id}
        value={value ?? ''}
        onChange={(event) => onChange?.(event.target.value)}
        disabled={disabled || parties.isLoading}
        required={required}
      >
        {allowEmpty && <option value="">— Select {isVendor ? 'vendor' : 'customer'} —</option>}
        {rows.map((party) => (
          <option key={party.id} value={party.id}>
            {isVendor
              ? party.name || party.legal_name
              : party.billing_name || party.legal_name || party.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ProjectSelector({ value, onChange, disabled = false, id, allowEmpty = true }) {
  const projects = useQuery(
    queryKeys.erpProjects,
    () => api.get('/projects', { params: { status: 'active' } }).then((r) => r.data),
    { staleTime: staleTimes.reference }
  );
  return (
    <select
      id={id}
      value={value ?? ''}
      onChange={(event) => onChange?.(event.target.value)}
      disabled={disabled || projects.isLoading}
    >
      {allowEmpty && <option value="">— No project —</option>}
      {(projects.data || []).map((project) => (
        <option key={project.id} value={project.id}>
          {project.code ? `${project.code} · ` : ''}{project.name}
        </option>
      ))}
    </select>
  );
}

export function GlPostingStatus({ status = 'unposted', journalEntryNumber, cashStatus }) {
  // Prefer human journal numbers (JE-00001); never show raw journal UUID ids in the UI.
  const label =
    journalEntryNumber &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      String(journalEntryNumber)
    )
      ? String(journalEntryNumber)
      : null;
  return (
    <span className="commercial-status-stack">
      <StatusBadge status={status} />
      {label && <span className="erp-code">{label}</span>}
      {cashStatus && cashStatus !== 'unpaid' && <StatusBadge status={cashStatus} />}
    </span>
  );
}

export function StatusLifecycle({ stages, current }) {
  const activeIndex = Math.max(0, stages.findIndex((stage) => stage.id === current));
  return (
    <ol className="commercial-lifecycle" aria-label="Document lifecycle">
      {stages.map((stage, index) => (
        <li
          key={stage.id}
          className={index < activeIndex ? 'is-complete' : index === activeIndex ? 'is-current' : ''}
          aria-current={index === activeIndex ? 'step' : undefined}
        >
          {stage.label}
        </li>
      ))}
    </ol>
  );
}

export function DocumentLinesEditor({ lines, onChange, unit = 'dollars', disabled = false }) {
  const update = (index, field, value) => {
    const next = lines.map((line, lineIndex) =>
      lineIndex === index ? { ...line, [field]: value } : line
    );
    onChange(next);
  };
  return (
    <div className="commercial-lines">
      {lines.map((line, index) => (
        <div className="commercial-line" key={line.id || index}>
          <input
            aria-label={`Line ${index + 1} description`}
            value={line.description || ''}
            onChange={(event) => update(index, 'description', event.target.value)}
            disabled={disabled}
          />
          <input
            aria-label={`Line ${index + 1} quantity`}
            type="number"
            min="0"
            step="0.01"
            value={line.quantity ?? 1}
            onChange={(event) => update(index, 'quantity', event.target.value)}
            disabled={disabled}
          />
          <input
            aria-label={`Line ${index + 1} unit price in ${unit}`}
            type="number"
            min="0"
            step={unit === 'cents' ? '1' : '0.01'}
            value={line.unit_price ?? line.unit_price_cents ?? ''}
            onChange={(event) =>
              update(index, unit === 'cents' ? 'unit_price_cents' : 'unit_price', event.target.value)
            }
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
}
